/**
 * Similar as UserInviteOptions.tsx just without
 * the option to remove the existing users via the input box,
 * and uses Member instead of TeamMember type.
 */
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Maybe} from 'graphql/jsutils/Maybe';
import React, {FC} from 'react';
import {components, MultiValue, OptionProps} from 'react-select';
import {FilterOptionOption} from 'react-select/dist/declarations/src/filters'; // eslint-disable-line import/no-unresolved

import {UserAccountType} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {checkIsServiceAccount} from '../../../util/serviceAccounts';
import {useGetRoleOptions} from '../../MembersTable/TeamMembersTable/useGetRoleOptions';

export type Member = {
  id?: Maybe<string>;
  username?: Maybe<string>;
  photoUrl?: Maybe<string>;
  name: string;
  email?: Maybe<string>;
  accountType?: Maybe<string>;
  role?: Maybe<string>;
};

export interface MemberInviteOption {
  value: string;
  label: string;
  username: Maybe<string>;
  photoUrl: Maybe<string>;
  name: Maybe<string>;
  email: Maybe<string>;
  accountType: Maybe<UserAccountType>;
}

interface MemberInviteOptionsProps {
  currentMembersIds: string[];
  entityMembers: Member[];
  selectedUsers: string[];
  setSelectedUsers: (users: string[]) => void;
}

const customFilter = (
  option: FilterOptionOption<MemberInviteOption>,
  inputValue: string
): boolean => {
  const input = inputValue.toLowerCase();
  const matchesName = option.data.name?.toLowerCase().includes(input);
  if (matchesName) {
    return true;
  }
  const matchesUsername = option.data.username?.toLowerCase().includes(input);
  if (matchesUsername) {
    return true;
  }
  const matchesEmail = option.data.email?.toLowerCase().includes(input);
  if (matchesEmail) {
    return true;
  }
  return false;
};

export const MemberInviteOptions: FC<MemberInviteOptionsProps> = ({
  currentMembersIds,
  entityMembers,
  selectedUsers,
  setSelectedUsers,
}) => {
  const viewer = useViewer();
  if (!viewer?.admin) {
    entityMembers = entityMembers.filter(
      u => u.id && u.id !== viewer?.id && !currentMembersIds.includes(u.id)
    );
  }
  const options: MemberInviteOption[] = entityMembers
    .filter(u => u.id && !currentMembersIds.includes(u.id))
    .map(u => {
      return {
        value: u.id as string,
        label: u.name,
        username: u.username,
        photoUrl: u.photoUrl,
        name: u.name,
        email: u.email,
        accountType: u.accountType as UserAccountType,
      };
    });

  const currentValue = options.filter(u => selectedUsers.includes(u.value));

  return (
    <Select<MemberInviteOption, true>
      className={'w-full'}
      size={'variable'}
      value={currentValue}
      options={options}
      placeholder={'Email or username'}
      isMulti
      isSearchable
      filterOption={customFilter}
      closeMenuOnSelect={false}
      onChange={(opt: MultiValue<MemberInviteOption>) =>
        setSelectedUsers(opt.map(o => o.value) ?? [])
      }
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        Option: MultiMemberInviteOptionComp,
      }}
    />
  );
};

const MultiMemberInviteOptionComp: React.FC<
  OptionProps<MemberInviteOption, true>
> = props => {
  const {data} = props;
  const {photoUrl, username, email, name} = data;

  return (
    <components.Option {...props}>
      <MemberInviteOptionComp
        photoUrl={photoUrl}
        name={name}
        username={username}
        email={email}
      />
    </components.Option>
  );
};

export const MemberInviteOptionComp: React.FC<
  Pick<MemberInviteOption, 'photoUrl' | 'username' | 'email' | 'name'>
> = ({photoUrl, username, email, name}) => {
  return (
    <Tailwind>
      <div className={'flex items-center'}>
        <img
          src={photoUrl ?? '/default-profile-picture.png'}
          className="mr-12 h-36 w-36 rounded-full"
          alt={`${name}'s profile picture`}
        />
        <div className={'flex flex-col items-center'}>
          <div className={'self-start'}>
            <span>{name}</span>
            {username && (
              <span className={'ml-6 text-sm text-moon-500'}>@{username}</span>
            )}
          </div>
          <span className={'-mt-2 self-start text-sm text-moon-500'}>
            {email}
          </span>
        </div>
      </div>
    </Tailwind>
  );
};

interface UserRoleItemProps {
  user: Member;
  isOwner?: boolean;
  entityName: string;
}

export const UserRoleItem: FC<UserRoleItemProps> = ({
  user,
  isOwner,
  entityName,
}) => {
  let userRole: string = user.role ?? '';
  const {roleOptions} = useGetRoleOptions(entityName);

  const viewer = useViewer();
  const userIsViewer = user.id === viewer?.id;

  const defaultRoleOption = roleOptions.find(
    roleOption => roleOption.value === userRole
  );
  const defaultRoleName = defaultRoleOption?.text;

  if (checkIsServiceAccount(user.accountType)) {
    userRole = 'Service Account';
  } else if (isOwner) {
    userRole = 'Owner';
  } else if (defaultRoleName) {
    userRole = defaultRoleName;
  }

  return (
    <Tailwind>
      <div className={'mt-12 flex items-center'}>
        <img
          src={user.photoUrl ?? '/default-profile-picture.png'}
          className="mr-12 h-36 w-36 rounded-full"
          alt={`${user.name}'s profile picture`}
        />
        <span>
          {user.name}
          {userIsViewer && ' (you)'}
        </span>
        <span className={'ml-auto'}>{userRole}</span>
      </div>
    </Tailwind>
  );
};
